import React, { useEffect } from 'react';
import { Box, CssBaseline, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, ListSubheader, Avatar } from '@mui/material';
import logoWhiteHorizontal from '../../assets/images/logo-white-horizantal.png';
import { NavLink, Outlet, Link as RouterLink } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  Tour as TourIcon,
  Article as ArticleIcon,
  Group as GroupIcon,
  BookOnline as BookOnlineIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
  Email as EmailIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Description as DescriptionIcon,
  Security as SecurityIcon,
  Cancel as CancelIcon,
  Help as HelpIcon,
  Web as FooterIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const drawerWidth = 260;

interface AdminLayoutProps {
  children: React.ReactNode;
}

// Menü kategorileri
const menuCategories = [
  {
    title: 'Ana Panel',
    items: [
      { text: 'Dashboard', path: '/admin', icon: <DashboardIcon /> },
    ]
  },
  {
    title: 'İçerik Yönetimi',
    items: [
      { text: 'Turlar', path: '/admin/tours', icon: <TourIcon /> },
      { text: 'Blog', path: '/admin/blogs', icon: <ArticleIcon /> },
      { text: 'Ana Sayfa', path: '/admin/homepage', icon: <HomeIcon /> },
      { text: 'Hakkımızda', path: '/admin/about', icon: <InfoIcon /> },
    ]
  },
  {
    title: 'Müşteri Yönetimi',
    items: [
      { text: 'Rezervasyonlar', path: '/admin/bookings', icon: <BookOnlineIcon /> },
      { text: 'Kullanıcılar', path: '/admin/users', icon: <PeopleIcon /> },
      { text: 'Özel Tur Talepleri', path: '/admin/custom-tour-requests', icon: <TourIcon /> },
    ]
  },
  {
    title: 'İletişim',
    items: [
      { text: 'İletişim Sayfası', path: '/admin/contact', icon: <ContactIcon /> },
      { text: 'Mesajlar', path: '/admin/messages', icon: <EmailIcon /> },

    ]
  },
  {
    title: 'Sayfa Yönetimi',
    items: [
      { text: 'SSS', path: '/admin/faq', icon: <QuestionAnswerIcon /> },
      { text: 'Rezervasyon Koşulları', path: '/admin/booking-terms', icon: <DescriptionIcon /> },
      { text: 'Gizlilik Politikası', path: '/admin/privacy-policy', icon: <SecurityIcon /> },
      { text: 'İptal ve İade', path: '/admin/cancellation-refund', icon: <CancelIcon /> },
      { text: 'Yardım Merkezi', path: '/admin/help-center', icon: <HelpIcon /> },
      { text: 'Footer', path: '/admin/footer', icon: <FooterIcon /> },
    ]
  },
  {
    title: 'Ayarlar',
    items: [
      { text: 'SEO', path: '/admin/seo', icon: <SearchIcon /> },
      { text: 'Site Ayarları', path: '/admin/settings', icon: <SecurityIcon /> },

    ]
  }
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== 'tr') {
      i18n.changeLanguage('tr');
    }
  }, [i18n]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ 
          width: `calc(100% - ${drawerWidth}px)`, 
          ml: `${drawerWidth}px`, 
          background: 'var(--gradient-primary)',
          boxShadow: 'var(--shadow-md)',
          borderBottom: 'none',
          borderRadius: '0 0 0 0'
        }}
      >
        <Toolbar sx={{ minHeight: '70px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="h5" noWrap component="div" sx={{ fontWeight: 600, color: 'white' }}>
              Hattravel Admin Paneli
            </Typography>
            <Avatar sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white',
              width: 40,
              height: 40,
              fontSize: '1.1rem',
              fontWeight: 600
            }}>
              A
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'var(--gradient-sidebar)',
            color: 'var(--color-beyaz)',
            borderRight: 'none',
            boxShadow: 'var(--shadow-xl)',
            borderRadius: 0
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box sx={{ 
          height: '70px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '0 0 0 0',
          mb: 1,
          px: 2
        }}>
          <img 
            src={logoWhiteHorizontal} 
            alt="Hattravel Logo" 
            style={{ 
              height: '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
        
        <Box sx={{ overflow: 'auto', px: 1, pb: 2 }}>
          {menuCategories.map((category, categoryIndex) => (
            <Box key={category.title} sx={{ mb: 2 }}>
              <ListSubheader 
                sx={{ 
                  backgroundColor: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  px: 2,
                  py: 1,
                  lineHeight: 1.2
                }}
              >
                {category.title}
              </ListSubheader>
              <List disablePadding>
                {category.items.map((item) => (
                  <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton 
                      component={RouterLink} 
                      to={item.path}
                      sx={{
                        borderRadius: '8px',
                        mx: 1,
                        py: 1,
                        transition: 'var(--transition-normal)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transform: 'translateX(4px)',
                        },
                        '&.active': {
                          backgroundColor: 'rgba(255,255,255,0.15)',
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'white',
                            fontWeight: 600,
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: 'rgba(255,255,255,0.8)',
                        minWidth: '36px',
                        transition: 'var(--transition-normal)'
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        primaryTypographyProps={{ component: 'div' }}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.9rem',
                            color: 'rgba(255,255,255,0.9)',
                            transition: 'var(--transition-normal)'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {categoryIndex < menuCategories.length - 1 && (
                <Divider sx={{ 
                  mx: 2, 
                  my: 1, 
                  borderColor: 'rgba(255,255,255,0.1)' 
                }} />
              )}
            </Box>
          ))}
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1, 
          bgcolor: 'var(--color-acik-beyaz)', 
          p: 3, 
          minHeight: '100vh',
          transition: 'var(--transition-normal)'
        }}
      >
        <Toolbar sx={{ minHeight: '70px !important' }} />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout; 